import React, { useState, useEffect } from 'react';
import { 
  HeartIcon, 
  UserIcon, 
  MagnifyingGlassIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { patientAPI, visitAPI } from '../../api';

const VitalSigns = () => {
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [vitalsData, setVitalsData] = useState({
    temperature: '',
    bloodPressure: '',
    pulse: '',
    respiration: '',
    oxygenSaturation: '',
    height: '',
    weight: '',
    notes: ''
  });

  useEffect(() => {
    loadPatients();
  }, []);

  const loadPatients = async () => {
    try {
      setLoading(true);
      const response = await patientAPI.getPatients({ search: searchQuery });
      setPatients(response.data?.patients || []);
    } catch (error) {
      console.error('Error loading patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRecordVitals = async () => {
    if (!selectedPatient) return;
    
    try {
      const visitData = {
        patient: selectedPatient._id,
        doctor: selectedPatient.assignedDoctor,
        vitals: {
          temperature: parseFloat(vitalsData.temperature) || undefined,
          bpSys: vitalsData.bloodPressure ? parseInt(vitalsData.bloodPressure.split('/')[0]) : undefined,
          bpDia: vitalsData.bloodPressure ? parseInt(vitalsData.bloodPressure.split('/')[1]) : undefined,
          pulse: parseInt(vitalsData.pulse) || undefined,
          respiration: parseInt(vitalsData.respiration) || undefined,
          oxygenSaturation: parseInt(vitalsData.oxygenSaturation) || undefined,
          heightCm: parseFloat(vitalsData.height) || undefined,
          weightKg: parseFloat(vitalsData.weight) || undefined
        },
        remarks: vitalsData.notes
      };
      
      await visitAPI.createVisit(visitData);
      
      // Reset form
      setVitalsData({
        temperature: '',
        bloodPressure: '',
        pulse: '',
        respiration: '',
        oxygenSaturation: '',
        height: '',
        weight: '',
        notes: ''
      });
      
      alert('Vital signs recorded successfully!');
    } catch (error) {
      console.error('Error recording vitals:', error);
      alert('Error recording vital signs. Please try again.');
    }
  };

  const getVitalStatus = (vital, value) => {
    const ranges = {
      temperature: { min: 97, max: 99.5, unit: '°F' },
      pulse: { min: 60, max: 100, unit: 'bpm' },
      respiration: { min: 12, max: 20, unit: '/min' },
      oxygenSaturation: { min: 95, max: 100, unit: '%' }
    };
    
    const range = ranges[vital];
    if (!range || !value) return 'normal';
    
    const numValue = parseFloat(value);
    if (numValue < range.min || numValue > range.max) return 'abnormal';
    return 'normal';
  };

  const PatientCard = ({ patient }) => (
    <div 
      className={`
        p-4 rounded-lg border cursor-pointer transition-all duration-200
        ${selectedPatient?._id === patient._id 
          ? 'border-blue-500 bg-blue-50 shadow-md' 
          : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
        }
      `}
      onClick={() => setSelectedPatient(patient)}
    >
      <div className="flex items-center">
        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
          <UserIcon className="w-5 h-5 text-blue-600" />
        </div>
        <div className="ml-3">
          <h4 className="font-medium text-gray-900">
            {patient.firstName} {patient.lastName}
          </h4>
          <p className="text-sm text-gray-500">ID: {patient.externalId}</p>
          <p className="text-xs text-gray-400">{patient.mobile}</p>
        </div>
      </div>
    </div>
  );

  const VitalInput = ({ label, value, onChange, type = "text", unit, placeholder, vital }) => {
    const status = getVitalStatus(vital, value);
    
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {unit && <span className="text-gray-500">({unit})</span>}
        </label>
        <div className="relative">
          <input
            type={type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            className={`
              w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500
              ${status === 'abnormal' ? 'border-red-300 bg-red-50' : 'border-gray-300'}
            `}
          />
          {value && status === 'abnormal' && (
            <ExclamationTriangleIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500" />
          )}
          {value && status === 'normal' && (
            <CheckCircleIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
          )}
        </div>
        {value && status === 'abnormal' && (
          <p className="text-xs text-red-600 mt-1">Value outside normal range</p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vital Signs</h1>
          <p className="text-gray-600">Record and monitor patient vital signs</p>
        </div>
        <div className="flex items-center">
          <HeartIcon className="w-8 h-8 text-red-500" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Patient Selection */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Patient</h3>
            
            {/* Search */}
            <div className="relative mb-4">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search patients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            {/* Patient List */}
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {loading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : patients.length > 0 ? (
                patients.map((patient) => (
                  <PatientCard key={patient._id} patient={patient} />
                ))
              ) : (
                <div className="text-center py-8">
                  <UserIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No patients found</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Vital Signs Form */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Record Vital Signs
              {selectedPatient && (
                <span className="text-sm font-normal text-gray-600 ml-2">
                  for {selectedPatient.firstName} {selectedPatient.lastName}
                </span>
              )}
            </h3>
            
            {selectedPatient ? (
              <form onSubmit={(e) => { e.preventDefault(); handleRecordVitals(); }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Primary Vitals */}
                  <VitalInput
                    label="Temperature"
                    value={vitalsData.temperature}
                    onChange={(e) => setVitalsData({...vitalsData, temperature: e.target.value})}
                    type="number"
                    step="0.1"
                    unit="°F"
                    placeholder="98.6"
                    vital="temperature"
                  />
                  
                  <VitalInput
                    label="Blood Pressure"
                    value={vitalsData.bloodPressure}
                    onChange={(e) => setVitalsData({...vitalsData, bloodPressure: e.target.value})}
                    unit="mmHg"
                    placeholder="120/80"
                  />
                  
                  <VitalInput
                    label="Pulse Rate"
                    value={vitalsData.pulse}
                    onChange={(e) => setVitalsData({...vitalsData, pulse: e.target.value})}
                    type="number"
                    unit="bpm"
                    placeholder="72"
                    vital="pulse"
                  />
                  
                  <VitalInput
                    label="Respiration Rate"
                    value={vitalsData.respiration}
                    onChange={(e) => setVitalsData({...vitalsData, respiration: e.target.value})}
                    type="number"
                    unit="/min"
                    placeholder="16"
                    vital="respiration"
                  />
                  
                  <VitalInput
                    label="Oxygen Saturation"
                    value={vitalsData.oxygenSaturation}
                    onChange={(e) => setVitalsData({...vitalsData, oxygenSaturation: e.target.value})}
                    type="number"
                    unit="%"
                    placeholder="98"
                    vital="oxygenSaturation"
                  />
                  
                  <div></div> {/* Spacer */}
                  
                  {/* Physical Measurements */}
                  <VitalInput
                    label="Height"
                    value={vitalsData.height}
                    onChange={(e) => setVitalsData({...vitalsData, height: e.target.value})}
                    type="number"
                    step="0.1"
                    unit="cm"
                    placeholder="170"
                  />
                  
                  <VitalInput
                    label="Weight"
                    value={vitalsData.weight}
                    onChange={(e) => setVitalsData({...vitalsData, weight: e.target.value})}
                    type="number"
                    step="0.1"
                    unit="kg"
                    placeholder="70"
                  />
                </div>
                
                {/* Notes */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Notes
                  </label>
                  <textarea
                    value={vitalsData.notes}
                    onChange={(e) => setVitalsData({...vitalsData, notes: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Any additional observations or notes..."
                  />
                </div>
                
                {/* Submit Button */}
                <div className="mt-8 flex justify-end">
                  <button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg flex items-center"
                  >
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Record Vital Signs
                  </button>
                </div>
              </form>
            ) : (
              <div className="text-center py-12">
                <HeartIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Patient Selected</h3>
                <p className="text-gray-500">Please select a patient from the list to record vital signs.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VitalSigns;